import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import {
  Alert,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { toastError } from "../../utils/util";
import CustomCircularP from "../../widgets/CustomCircularP";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import { printRow } from "./ImtexBadgeDimension";
import { useNavigate, useParams } from "react-router-dom";
import { getOnsiteHeader, isOnsiteActivated } from "./utils";
import useCommonGetInit from "../../hooks/useCommonGetInit";
import MuPb from "../../widgets/MuPb";

function SelfPrinting() {
  const navigate = useNavigate();

  // Local loading & message states
  const [isLoading, setIsLoading] = useState(false);
  const [printMessage, setPrintMessage] = useState("");

  // Dialog state for reprint confirmation
  const [showReprintDialog, setShowReprintDialog] = useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);

  const inputRef = useRef();

  // Handle "Enter" key press in the input field
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const scannedValue = event.target.value.trim();
      if (scannedValue) {
        fetchUserDetails(scannedValue);
      }
      // Clear and refocus
      inputRef.current.value = "";
      inputRef.current?.focus();
    }
  };

  // Fetch user details and print
  const fetchUserDetails = async (bid) => {
    try {
      setIsLoading(true);
      setPrintMessage("Please wait... Fetching details.");

      const { data } = await axios.get(
        `${API_ENDPOINT}/onsite/get-scanned-visitor-kiosk?barcode_id=${bid}`,
        getOnsiteHeader(ONSITE_MODULE_ITEMS.KIOSK_BADGE_PRINTING)
      );

      // Check if badge was already printed
      if (data?.badgePrinted) {
        setPendingUserData(data);
        setShowReprintDialog(true);
        setIsLoading(false);
        setPrintMessage("");
      } else {
        // Proceed with printing
        setPrintMessage("Please wait... Printing badge.");
        printRow(data);
        setIsLoading(false);
        setPrintMessage("");

        // Reset & focus the input again
        if (inputRef.current) {
          inputRef.current.value = "";
          inputRef.current.focus();
        }
      }
    } catch (err) {
      toastError(err);
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.focus();
      }
      setIsLoading(false);
      setPrintMessage("");
    }
  };

  // Handle reprint confirmation
  const handleReprintConfirm = () => {
    setShowReprintDialog(false);
    setIsLoading(true);
    setPrintMessage("Please wait... Printing badge.");

    try {
      printRow(pendingUserData);
    } catch (err) {
      toastError(err);
    } finally {
      setIsLoading(false);
      setPrintMessage("");
      setPendingUserData(null);

      // Reset & focus the input again
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.focus();
      }
    }
  };

  // Handle reprint cancellation
  const handleReprintCancel = () => {
    setShowReprintDialog(false);
    setPendingUserData(null);

    // Reset & focus the input again
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  }, []);

  return (
    <Box>
      {/* Existing loading spinner */}
      <CustomCircularP show={isLoading} />

      {/* Show a message if we have one (e.g. while printing) */}
      {printMessage && (
        <Typography variant="subtitle1" textAlign="center" color="secondary">
          {printMessage}
        </Typography>
      )}

      <Typography variant="h5" textAlign="center" mt={2} mb={2}>
        The Cursor should be in the Input Field while Scanning.
      </Typography>

      <Box display="table" ml="auto" mr="auto">
        <input
          ref={inputRef}
          id="barcodeInput"
          type="text"
          onKeyDown={handleKeyDown}
          placeholder="Scan QR Code"
          style={{
            padding: "10px",
            fontSize: "16px",
            width: "300px",
            textAlign: "center",
            borderRadius: "4px",
            border: "2px solid #000",
          }}
        />
      </Box>

      {/* Reprint Confirmation Dialog */}
      <Dialog
        open={showReprintDialog}
        onClose={handleReprintCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Badge Already Printed</DialogTitle>
        <DialogContent>
          <Typography>
            This badge has already been printed. Do you want to print it again?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReprintCancel} color="inherit">
            No
          </Button>
          <Button
            onClick={handleReprintConfirm}
            color="primary"
            variant="contained"
          >
            Yes, Print Again
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SelfPrinting;
